import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TablePagination,
  Button,
  Grid,
  Card,
  Menu,
  MenuItem,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import EditIcon from "@mui/icons-material/Edit";
import PaymentEditModal from "./PaymentEditModal";
import { useSelector } from "react-redux";
import { useFetch } from "../../hooks";
import { urls } from "../../urls/urls";
import { BASE_URL_TEST } from "../../utils/baseUrl";

const statusColors = {
  Pending: "#FFC60029",
  "On process": "#BBDEFB",
  Complete: "#C8E6C9",
};

export default function Payments() {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [vendorRequests, setVendorRequests] = useState([]);
  const apptoken = useSelector((state) => state.user.apptoken);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const { data, isLoading, fetchData } = useFetch(
    BASE_URL_TEST + urls("getRegularVendorPayments"),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: apptoken ? `Bearer ${apptoken}` : "",
      },
    },
  );

  const {
    data: requestsData,
    isLoading: requestsLoading,
    fetchData: fetchRequests,
  } = useFetch(BASE_URL_TEST + urls("getAllRequestsAdmin", "pending"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });
  const handleSortMenuOpen = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (field) => {
    if (sortField === field) {
      // Toggle asc/desc if same field clicked
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setSortAnchorEl(null);
    setPage(0);
  };
  useEffect(() => {
    fetchData();
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && data.status) {
      setPaymentsData(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (requestsData && requestsData.status) {
      setVendorRequests(requestsData.data || []);
    }
  }, [requestsData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const summaryData = paymentsData
    ? [
        {
          title: "Total Vendors",
          value: paymentsData.total_vendors,
          growth: `${paymentsData.total_vendors} Vendors`,
          color: "#4CAF50",
          icon: <StorefrontIcon fontSize="large" />,
        },
        {
          title: "Total Orders Amount",
          value: formatCurrency(paymentsData.total_orders_amount),
          growth: "Total Amount",
          color: "#1E88E5",
          icon: <AccountBalanceWalletIcon fontSize="large" />,
        },
        {
          title: "Total Withdrawn",
          value: formatCurrency(paymentsData.total_withdrawn),
          growth: `${paymentsData.completed_withdrawals_count} Completed`,
          color: "#26A69A",
          icon: <ArrowCircleDownIcon fontSize="large" />,
        },
        {
          title: "Pending Amount",
          value: formatCurrency(paymentsData.pending_amount),
          growth: `${paymentsData.pending_withdrawals_count} Pending`,
          color: "#F44336",
          icon: <PendingActionsIcon fontSize="large" />,
        },
      ]
    : [];

  const formatBankDetails = (bankDetails) => {
    if (!bankDetails || bankDetails.length === 0) return "N/A";
    const bank = bankDetails.find((b) => b.is_primary === 1) || bankDetails[0];
    return `${bank.bank_name} - ${bank.account_number}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const filteredOrders = vendorRequests
    .filter(
      (request) =>
        request.vendorDetails?.vendor_id
          ?.toLowerCase()
          .includes(searchValue.toLowerCase()) ||
        request.vendorDetails?.shop_name
          ?.toLowerCase()
          .includes(searchValue.toLowerCase()),
    )
    .sort((a, b) => {
      if (!sortField) return 0;

      let aVal, bVal;

      if (sortField === "vendor_id") {
        aVal = a.vendorDetails?.vendor_id || "";
        bVal = b.vendorDetails?.vendor_id || "";
      } else if (sortField === "shop_name") {
        aVal = a.vendorDetails?.shop_name || "";
        bVal = b.vendorDetails?.shop_name || "";
      } else if (sortField === "amount") {
        aVal = parseFloat(a.amount || 0);
        bVal = parseFloat(b.amount || 0);
      } else if (sortField === "requested_at") {
        aVal = new Date(a.requested_at || 0);
        bVal = new Date(b.requested_at || 0);
      } else if (sortField === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      }

      if (typeof aVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
    });

  const handlePaymentUpdateSuccess = () => {
    fetchData();
    fetchRequests();
  };

  const handleEditClick = (payment) => {
    setSelectedPayment(payment);
    setOpenEditModal(true);
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
    setSelectedPayment(null);
  };

  if (isLoading || requestsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          bgcolor: "#EEF2FF",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        bgcolor: "#EEF2FF",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid container spacing={1}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                bgcolor: item.color,
                color: "#fff",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  justifyContent={"space-between"}
                >
                  <Box>
                    <Typography variant="body1">{item.title}</Typography>
                    <Typography variant="h6">{item.value}</Typography>
                    <Typography variant="body2">{item.growth}</Typography>
                  </Box>
                  <Typography variant="h3">{item.icon}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          mb: 3,
          justifyContent: "flex-end",
          mt: 2,
        }}
      >
        <TextField
          placeholder="Search Here"
          variant="outlined"
          size="small"
          value={searchValue}
          onChange={handleSearchChange}
          sx={{
            width: "250px",
            borderRadius: "10px",
            backgroundColor: "#f5f7fa",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#d8dbe2",
              },
              "&:hover fieldset": {
                borderColor: "#aeb3bb",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#aeb3bb",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#8b94a3" }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#2c666e",
            color: "#fff",
            borderRadius: "8px",
            ml: 1,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#245757",
            },
          }}
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handleSortMenuOpen}
        >
          {sortField
            ? `Sort: ${
                sortField === "vendor_id"
                  ? "Vendor ID"
                  : sortField === "shop_name"
                    ? "Shop Name"
                    : sortField === "amount"
                      ? "Amount"
                      : sortField === "requested_at"
                        ? "Date"
                        : sortField === "status"
                          ? "Status"
                          : ""
              } (${sortOrder === "asc" ? "↑" : "↓"})`
            : "Sort By"}
        </Button>

        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortMenuClose}
        >
          <MenuItem onClick={() => handleSortSelect("vendor_id")}>
            Vendor ID{" "}
            {sortField === "vendor_id" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("shop_name")}>
            Shop Name{" "}
            {sortField === "shop_name" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("amount")}>
            Amount{" "}
            {sortField === "amount" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("requested_at")}>
            Requested Date{" "}
            {sortField === "requested_at"
              ? sortOrder === "asc"
                ? "↑"
                : "↓"
              : ""}
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect("status")}>
            Status{" "}
            {sortField === "status" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
          </MenuItem>
          {sortField && (
            <MenuItem
              onClick={() => {
                setSortField("");
                setSortOrder("asc");
                setSortAnchorEl(null);
              }}
              sx={{ color: "red", borderTop: "1px solid #eee" }}
            >
              Clear Sort
            </MenuItem>
          )}
        </Menu>
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: "20px", boxShadow: 1 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F5F5F5", fontWeight: "bold" }}>
              <TableCell sx={{ fontWeight: "bold" }}>S.NO</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>VENDOR ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>SHOP NAME</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>BANK DETAILS</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>AMOUNT</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>REQUESTED DATE</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>STATUS</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                ACTION
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 4, color: "#666" }}>
                    No pending vendor payment requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((request, index) => (
                  <TableRow key={request.vpayment_id || index}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      {request.vendorDetails?.vendor_id || "N/A"}
                    </TableCell>
                    <TableCell>
                      {request.vendorDetails?.shop_name || "N/A"}
                    </TableCell>
                    <TableCell>
                      {formatBankDetails(request.vendorDetails?.bankDetails)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(parseFloat(request.amount || 0))}
                    </TableCell>
                    <TableCell>{formatDate(request.requested_at)}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor:
                            statusColors[
                              request.status?.charAt(0).toUpperCase() +
                                request.status?.slice(1)
                            ] || "#FFC60029",
                          borderRadius: "20px",
                          padding: "6px 16px",
                          color:
                            request.status === "pending"
                              ? "#FFC600"
                              : request.status === "complete"
                                ? "#4CAF50"
                                : "#2196F3",
                          fontWeight: "500",
                          display: "inline-block",
                          textTransform: "capitalize",
                        }}
                      >
                        {request.status}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(request)}
                        sx={{
                          textTransform: "none",
                          borderColor: "#2c666e",
                          color: "#2c666e",
                          borderRadius: "8px",
                          "&:hover": {
                            borderColor: "#245757",
                            bgcolor: "#f5f5f5",
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) =>
            setRowsPerPage(parseInt(event.target.value, 10))
          }
          sx={{ p: 2, bgcolor: "#F5F5F5", borderTop: "1px solid #E0E0E0" }}
        />
      </TableContainer>

      <PaymentEditModal
        key={selectedPayment?.vpayment_id || "new"}
        open={openEditModal}
        onClose={handleCloseModal}
        paymentData={selectedPayment}
        onSuccess={handlePaymentUpdateSuccess}
      />
    </Box>
  );
}
